import { subDays } from 'date-fns';
import { type SourcingOpenings } from 'api-clients/monolith';

import { getRangeTimeZoneLabel, localDayAt } from '@/utils/time-zone';

/**
 * The zone label for a Dashboard row, named for the window its metrics count:
 * the hiring goal's days, or else the account's default last N days ending today
 * at the opening. A window with no end, or the lifetime default, has no single
 * abbreviation, so it names the zone alone.
 * @param opening - The row. eg { time_zone: 'America/Chicago', hiring_goal: null, … }
 * @param defaultMetricsPeriod - The account's default days, or null for lifetime. eg 30
 * @param now - The current instant. eg new Date()
 * @returns The label, or null for an unknown zone. eg 'America/Chicago (CDT)'
 */
export function getRowTimeZoneLabel(
  opening: Pick<SourcingOpenings, 'time_zone' | 'hiring_goal'>,
  defaultMetricsPeriod: number | null | undefined,
  now: Date,
): string | null {
  const timeZone = opening.time_zone;
  const goal = opening.hiring_goal;

  if (goal) {
    const startDay = localDayAt(new Date(goal.start_date), timeZone);
    if (!startDay) return null;
    const endDay = goal.hire_by
      ? localDayAt(new Date(goal.hire_by), timeZone)
      : null;
    return endDay
      ? getRangeTimeZoneLabel(startDay, endDay, timeZone)
      : timeZone;
  }

  const today = localDayAt(now, timeZone);
  if (!today) return null;
  if (defaultMetricsPeriod == null) return timeZone;
  return getRangeTimeZoneLabel(
    subDays(today, defaultMetricsPeriod - 1),
    today,
    timeZone,
  );
}
